import { useCallback, useEffect, useRef } from 'react';
import { useIsMounted } from '~/hooks';

async function streamResponse({
  url,
  payload,
  signal,
  onDelta,
  onChart,
  onTable,
  onStatusText,
  onToolDetected,
  onToolComplete,
  onStatusUpdate,
  onComplete,
}) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    let message = 'Something went wrong. Please try again.';
    try {
      const body = await res.json();
      if (body.message) message = body.message;
    } catch {
      // response may not be JSON
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const chunk of parts) {
      const lines = chunk.split('\n');

      let eventName = 'message';
      let dataLine = null;

      for (const line of lines) {
        if (line.startsWith('event:')) eventName = line.replace(/^event:\s*/, '').trim();
        if (line.startsWith('data:')) dataLine = line.replace(/^data:\s*/, '').trim();
      }

      if (!dataLine) continue;

      const data = JSON.parse(dataLine);

      switch (eventName) {
        case 'text':
          onDelta?.(data.delta);
          break;

        case 'chart':
          onChart?.(data);
          break;

        case 'statusText':
          onStatusText?.(data.statusText);
          break;

        case 'toolDetected':
          onToolDetected?.(data);
          break;

        case 'toolComplete':
          onToolComplete?.(data);
          break;

        case 'table':
          onTable?.(data);
          break;

        case 'statusUpdate':
          onStatusUpdate?.(data);
          break;
      }
    }
  }

  onComplete?.();
}

export default function useStream({ setMessages, setThinking }) {
  const isMounted = useIsMounted();
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const run = useCallback(
    async (url, payload, { afterComplete } = {}) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setThinking(true);

      await streamResponse({
        url,
        payload,
        signal,

        onStatusText: () => {
          if (!isMounted.current) return;
          setThinking(true);
        },

        onToolDetected: ({ callId, toolName, statusText }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            let toolsPart = last.parts.find((p) => p.type === 'tools');
            if (!toolsPart) {
              toolsPart = { type: 'tools', tools: [] };
              last.parts.unshift(toolsPart);
            }
            if (!toolsPart.tools.some((t) => t.callId === callId)) {
              toolsPart.tools = [...toolsPart.tools, { callId, toolName, statusText, status: 'running' }];
            }
            return updated;
          });
        },

        onToolComplete: ({ callId, success }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            const toolsPart = last.parts.find((p) => p.type === 'tools');
            if (toolsPart) {
              toolsPart.tools = toolsPart.tools.map((t) =>
                t.callId === callId ? { ...t, status: success ? 'completed' : 'failed' } : t,
              );
            }
            return updated;
          });
        },

        onStatusUpdate: ({ callId, statusText }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            const toolsPart = last.parts.find((p) => p.type === 'tools');
            if (toolsPart) {
              toolsPart.tools = toolsPart.tools.map((t) => (t.callId === callId ? { ...t, statusText } : t));
            }
            return updated;
          });
        },

        onDelta: (delta) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            let textPart = last.parts.find((p) => p.type === 'text' && p._streaming);
            if (!textPart) {
              textPart = { type: 'text', content: '', _streaming: true };
              last.parts.push(textPart);
            }
            textPart.content += delta;

            return updated;
          });
        },

        onChart: (chartData) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts.push({ type: 'chart', ...chartData });
            return updated;
          });
        },

        onTable: ({ data, fields, meta, pagination, filename }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts.push({ type: 'table', data, fields, meta, pagination, filename });
            return updated;
          });
        },

        onComplete: async () => {
          if (!isMounted.current) return;

          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            last.parts.forEach((p) => delete p._streaming);
            return updated;
          });

          setThinking(false);
          await afterComplete?.();
        },
      });
    },
    [isMounted, setMessages, setThinking],
  );

  return { run, abort };
}
